import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./App.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation } from "@apollo/client/react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "./i18n/I18nProvider";
import { Language } from "./i18n/translations";
import { Plan } from "./types";
import {
  HouseholdType,
  LeadHousehold,
  LeadHouseholdMember,
  LeadWizardMetadata,
  WizardPrimaryApplicant,
  WizardStep,
  ConsentStatus,
  QuoteHolderFormState,
  QuoteMemberFormState,
  QuotePaymentFormState,
} from "./types/leadWizard";
import {
  parseMetadata,
  emptyMetadata,
  householdNeedsAdditionalMembers,
  isHouseholdComplete,
  primaryFromMetadata,
  identityStatusFromMetadata,
} from "./utils/leadMetadata";
import {
  PUBLIC_START_LEAD,
  PUBLIC_UPDATE_HOUSEHOLD,
  PUBLIC_CONFIRM_PLAN,
  PUBLIC_CHECK_CONSENT,
  PUBLIC_INIT_IDENTITY,
  PUBLIC_VERIFY_IDENTITY_OTP,
  PUBLIC_CREATE_QUOTE,
  PUBLIC_UPDATE_LEAD_STAGE,
} from "./gql/publicLead";
import {
  PublicCheckConsentResponse,
  PublicConfirmPlanResponse,
  PublicStartLeadResponse,
  PublicUpdateHouseholdResponse,
  PublicInitIdentityResponse,
  PublicVerifyIdentityOtpResponse,
  PublicCreateQuoteResponse,
  PublicUpdateLeadStageResponse,
} from "./gql/types/PublicLead";
import { HouseholdTypeStep } from "./components/HouseholdTypeStep";
import { CoverageTypeStep } from "./components/CoverageTypeStep";
import { HouseholdBasicsStep } from "./components/HouseholdBasicsStep";
import { PrimaryApplicantStep } from "./components/PrimaryApplicantStep";
import { AdditionalMembersStep } from "./components/AdditionalMembersStep";
import { PlanSelectionPage } from "./pages/PlanSelectionPage";
import { PlanSummaryPage } from "./components/PlanSummaryPage";
import { QuoteOtpStep } from "./components/QuoteOtpStep";
import { QuoteHolderStep } from "./components/QuoteHolderStep";
import { QuoteMembersStep } from "./components/QuoteMembersStep";
import { QuotePaymentStep } from "./components/QuotePaymentStep";
import { QuoteSuccessStep } from "./components/QuoteSuccessStep";
import { faPhone } from "@fortawesome/free-solid-svg-icons";
import { pushToDataLayer } from "./utils/gtm";
import { getRecaptchaToken } from "./utils/recaptcha";
import { decodePublicTokenConfig, getPublicAccessToken } from "./utils/publicToken";
import { OPTIONS } from "./consts";

const STEP_TITLE_KEYS: Record<WizardStep, string> = {
  householdType: "wizard.titles.householdType",
  householdBasics: "wizard.titles.householdBasics",
  primary: "wizard.titles.primary",
  members: "wizard.titles.members",
  identityOtp: "wizard.titles.identityOtp",
  plans: "wizard.titles.plans",
  summary: "wizard.titles.summary",
  consent: "wizard.titles.consent",
  extra: "wizard.titles.extra",
  quoteHolder: "wizard.titles.quoteHolder",
  quoteMembers: "wizard.titles.quoteMembers",
  quotePayment: "wizard.titles.quotePayment",
  quoteSuccess: "wizard.titles.quoteSuccess",
};

interface PlanSelectionContext {
  fetchedAt: string;
  count: number;
  year: number;
}

const HERO_CARRIER_LOGOS = [
  { src: "/media/UnitedHealthcare.png", alt: "UnitedHealthcare" },
  { src: "/media/cigna.png", alt: "Cigna" },
  { src: "/media/aetna.png", alt: "Aetna" },
  { src: "/media/humana.png", alt: "Humana" },
  { src: "/media/molinahealthcare.jpg", alt: "Molina Healthcare" },
  { src: "/media/oscar.jpg", alt: "Oscar" },
];

const buildPlanSelectionInput = (plan: Plan) => ({
  planId: plan.id,
  issuer: plan.carrier,
  premium: plan.monthlyPremium,
  deductible: plan.deductible,
  metalLevel: plan.metalLevel ?? null,
  productType: plan.productType ?? null,
  name: plan.name,
});

const consentFromMetadata = (metadata: LeadWizardMetadata): ConsentStatus => {
  const status = metadata.consent?.status;
  if (!status) {
    return "NOT_REQUESTED";
  }
  return status as ConsentStatus;
};

const quoteRelationshipFromRole = (role?: string | null) => {
  if (!role) return "Other Relationship";
  const normalized = role.toUpperCase();
  if (normalized === "SPOUSE") return "Spouse";
  if (normalized === "DEPENDENT") return "Child";
  if (normalized === "PRIMARY") return "Self";
  return "Other Relationship";
};

const buildQuoteHolderState = (
  metadata: LeadWizardMetadata,
  primary: WizardPrimaryApplicant | LeadHouseholdMember | null
): QuoteHolderFormState => {
  const household = metadata.household || {};
  const source = primary
    ? primary
    : primaryFromMetadata(metadata) ?? {
      firstName: "",
      lastName: "",
      gender: "F" as const,
      birthDate: "",
      email: "",
      phone: "",
      acceptsTerms: false,
      whatsappConsent: false,
    };

  const firstName = (source as any).firstName ?? "";
  const lastName = (source as any).lastName ?? "";
  const gender = ((source as any).gender ?? "F") as "M" | "F" | "X";
  const birthDate = (source as any).birthDate ?? "";
  const email = (source as any).email ?? "";
  const phone = (source as any).phone ?? "";

  const stateCode = (household.stateId as string | undefined) ?? "";
  const stateName = (household.stateName as string | undefined) ?? "";
  const zipCode = (household.zipCode as string | undefined) ?? "";

  return {
    firstName,
    lastName,
    gender,
    birthDate,
    email,
    phone,
    address1: "",
    address2: "",
    city: "",
    stateCode: stateCode.toUpperCase(),
    stateName,
    zipCode,
    countyFips: (household.countyFips as string | undefined) ?? "",
    ssn: "",
    usesTobacco: false,
    isPregnant: false,
  };
};

const buildQuoteMembersState = (
  metadata: LeadWizardMetadata
): QuoteMemberFormState[] => {
  const members = metadata.members || [];
  if (!members.length) {
    return [];
  }
  return members.slice(1).map((member) => ({
    firstName: member.firstName,
    lastName: member.lastName,
    gender: (member.gender as "M" | "F" | "X") ?? "F",
    birthDate: member.birthDate,
    relationship: quoteRelationshipFromRole(member.role),
    email: member.email ?? "",
    phone: member.phone ?? "",
    usesTobacco: false,
    isPregnant: false,
  }));
};

const buildQuotePaymentState = (
  holder: QuoteHolderFormState
): QuotePaymentFormState => ({
  method: "CARD",
  cardNumber: "",
  expirationMonth: "",
  expirationYear: "",
  cvv: "",
  cardType: "CREDIT_CARD",
  cardCompany: "",
  bankName: "",
  abaRouting: "",
  accountNumber: "",
  paymentHolder: `${holder.firstName} ${holder.lastName}`.trim(),
  billingAddress1: holder.address1,
  billingAddress2: holder.address2,
  billingCity: holder.city,
  billingStateCode: holder.stateCode,
  billingZipCode: holder.zipCode,
  confirmAccuracy: false,
  providePaymentLater: false,
});

const buildNoPaymentState = (
  holder: QuoteHolderFormState
): QuotePaymentFormState => ({
  method: "NONE",
  cardNumber: "",
  expirationMonth: "",
  expirationYear: "",
  cvv: "",
  cardType: "",
  cardCompany: "",
  bankName: "",
  abaRouting: "",
  accountNumber: "",
  paymentHolder: `${holder.firstName} ${holder.lastName}`.trim(),
  billingAddress1: holder.address1,
  billingAddress2: holder.address2,
  billingCity: holder.city,
  billingStateCode: holder.stateCode,
  billingZipCode: holder.zipCode,
  confirmAccuracy: true,
  providePaymentLater: true,
});

const RECENT_CITIES = [
  "Chicago, IL",
  "San Antonio, TX",
  "Tampa, FL",
  "Las Vegas, NV",
  "Miami, FL",
  "Houston, TX",
  "Orlando, FL",
  "Dallas, TX",
];

const App: React.FC = () => {
  const { t, language, setLanguage } = useTranslation();

  const [currentCityIndex, setCurrentCityIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCityIndex((prev) => (prev + 1) % RECENT_CITIES.length);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const [wizardStep, setWizardStep] = useState<WizardStep>("householdType");
  const [metadata, setMetadata] = useState<LeadWizardMetadata>(emptyMetadata());
  const [householdType, setHouseholdType] = useState<HouseholdType | null>(
    null
  );
  const [leadId, setLeadId] = useState<string | null>(null);
  const [primaryApplicant, setPrimaryApplicant] =
    useState<WizardPrimaryApplicant | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [planContext, setPlanContext] = useState<PlanSelectionContext | null>(
    null
  );
  const [signingLink, setSigningLink] = useState<string | null>(null);
  const [consentHtml, setConsentHtml] = useState<string | null>(null);
  const [consentStatus, setConsentStatus] =
    useState<ConsentStatus>("NOT_REQUESTED");
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingConsent, setIsCheckingConsent] = useState(false);
  const [identityToken, setIdentityToken] = useState<string | null>(null);
  const [identityOtpStatus, setIdentityOtpStatus] = useState({
    sent: false,
    smsSent: false,
    emailSent: false,
  });
  const [isIdentityOtpSending, setIsIdentityOtpSending] = useState(false);
  const [isIdentityOtpVerifying, setIsIdentityOtpVerifying] = useState(false);
  const consentTrackedLeadRef = useRef<string | null>(null);
  const leadStageRef = useRef<{ leadId: string | null; stageId: number | null }>(
    {
      leadId: null,
      stageId: null,
    }
  );

  const [quoteHolderState, setQuoteHolderState] =
    useState<QuoteHolderFormState>(() =>
      buildQuoteHolderState(metadata, primaryApplicant)
    );
  const [quoteMembersState, setQuoteMembersState] = useState<
    QuoteMemberFormState[]
  >(() => buildQuoteMembersState(metadata));
  const [quotePaymentState, setQuotePaymentState] =
    useState<QuotePaymentFormState>(() =>
      buildQuotePaymentState(buildQuoteHolderState(metadata, primaryApplicant))
    );
  const [isCreatingQuote, setIsCreatingQuote] = useState(false);
  const [quotePolicyId, setQuotePolicyId] = useState<string | null>(null);
  const [requiresPayment, setRequiresPayment] = useState<boolean>(true);

  const currentPrimary = primaryApplicant || primaryFromMetadata(metadata);

  const publicToken = useMemo(() => getPublicAccessToken(), []);

  const resolvedHouseholdType = (householdType ||
    (metadata.household.type as HouseholdType | null)) as HouseholdType | null;
  const identityVerified = identityStatusFromMetadata(metadata) === "VERIFIED";

  const planSelection = metadata.plan?.selection;
  const successPlanName =
    selectedPlan?.name ??
    (planSelection && typeof planSelection.name === "string"
      ? planSelection.name
      : null);
  const planSelectionCarrier =
    typeof planSelection?.issuer === "string"
      ? planSelection.issuer
      : typeof (planSelection as any)?.carrier === "string"
        ? (planSelection as any).carrier
        : null;
  const successPlanCarrier = selectedPlan?.carrier ?? planSelectionCarrier;
  const selectionPremiumRaw = planSelection?.premium;
  const selectionPremiumNumber =
    typeof selectionPremiumRaw === "number"
      ? selectionPremiumRaw
      : selectionPremiumRaw !== undefined && selectionPremiumRaw !== null
        ? Number(selectionPremiumRaw)
        : null;
  const successPlanPremium =
    selectedPlan?.monthlyPremium ??
    (selectionPremiumNumber !== null && Number.isFinite(selectionPremiumNumber)
      ? selectionPremiumNumber
      : null);

  useEffect(() => {
    if (
      ["quoteHolder", "quoteMembers", "quotePayment", "quoteSuccess"].includes(
        wizardStep
      )
    ) {
      return;
    }
    const baseHolder = buildQuoteHolderState(metadata, currentPrimary ?? null);
    setQuoteHolderState(baseHolder);
    setQuoteMembersState(buildQuoteMembersState(metadata));
    setQuotePaymentState(buildQuotePaymentState(baseHolder));
  }, [metadata, currentPrimary, wizardStep]);

  useEffect(() => {
    if (selectedPlan && typeof selectedPlan.monthlyPremium === "number") {
      setRequiresPayment(selectedPlan.monthlyPremium > 0);
      return;
    }

    const premium = metadata.plan?.selection?.premium;
    if (premium !== undefined && premium !== null) {
      setRequiresPayment(Number(premium) > 0);
    }
  }, [selectedPlan, metadata]);

  useEffect(() => {
    // console.log(wizardStep);
    if (!requiresPayment && wizardStep === "quotePayment") {
      setWizardStep(
        quoteMembersState.length > 0 ? "quoteMembers" : "quoteHolder"
      );
    }
  }, [requiresPayment, wizardStep, quoteMembersState.length]);

  useEffect(() => {
    if (!requiresPayment) {
      setQuotePaymentState((prev) => {
        const next = buildNoPaymentState(quoteHolderState);
        if (
          prev.method === next.method &&
          prev.paymentHolder === next.paymentHolder &&
          prev.billingAddress1 === next.billingAddress1 &&
          prev.billingAddress2 === next.billingAddress2 &&
          prev.billingCity === next.billingCity &&
          prev.billingStateCode === next.billingStateCode &&
          prev.billingZipCode === next.billingZipCode &&
          prev.confirmAccuracy === next.confirmAccuracy &&
          prev.providePaymentLater === next.providePaymentLater
        ) {
          return prev;
        }
        return next;
      });
    }
  }, [requiresPayment, quoteHolderState]);

  useEffect(() => {
    if (requiresPayment) {
      setQuotePaymentState((prev) =>
        prev.method === "NONE" ? buildQuotePaymentState(quoteHolderState) : prev
      );
    }
  }, [requiresPayment, quoteHolderState]);

  const buildPrimaryMember = (
    applicant: WizardPrimaryApplicant
  ): LeadHouseholdMember => ({
    role: "PRIMARY",
    firstName: applicant.firstName.trim(),
    lastName: applicant.lastName.trim(),
    gender: applicant.gender,
    birthDate: applicant.birthDate,
    email: applicant.email.trim(),
    phone: applicant.phone.trim(),
  });

  const mapMemberToInput = (member: LeadHouseholdMember) => ({
    role: member.role,
    firstName: member.firstName,
    lastName: member.lastName,
    gender: member.gender,
    birthDate: member.birthDate,
    email: member.email ?? null,
    phone: member.phone ?? null,
  });

  const submitStartLead = async (
    membersList: LeadHouseholdMember[],
    applicant: WizardPrimaryApplicant,
    householdPayload: LeadHousehold
  ): Promise<LeadWizardMetadata | null> => {
    setIsSubmitting(true);
    setApiError(null);

    try {
      const captchaToken = await getRecaptchaToken("public_start_lead");

      const additionalMembers = membersList.filter(
        (member) => member.role !== "PRIMARY"
      );
      const { data } = await startLeadMutation({
        variables: {
          token: publicToken,
          household: {
            type: householdPayload.type,
            zipCode: householdPayload.zipCode,
            income: householdPayload.income,
            countyFips: householdPayload.countyFips,
            countyName: householdPayload.countyName,
            stateId: householdPayload.stateId,
            stateName: householdPayload.stateName,
            effectiveDate: householdPayload.effectiveDate,
          },
          primary: {
            firstName: applicant.firstName,
            lastName: applicant.lastName,
            gender: applicant.gender,
            birthDate: applicant.birthDate,
            phone: applicant.phone,
            email: applicant.email,
            acceptsTerms: applicant.acceptsTerms,
            whatsappConsent: applicant.whatsappConsent,
          },
          members: additionalMembers.map(mapMemberToInput),
          context: {
            leadSourceId: leadConfig.leadSourceId,
            stagePipelineId: leadConfig.stagePipelineId,
            campusId: leadConfig.campusId,
          },
          captchaToken,
        },
      });

      const response = data?.publicStartLead;
      if (!response?.success || !response.leadId) {
        const message =
          response?.errors?.[0]?.message || t("wizard.errors.generic");
        setApiError(message);
        return null;
      }

      const nextMetadata = parseMetadata(response.metadata);
      setMetadata(nextMetadata);
      setLeadId(response.leadId);
      setConsentStatus(consentFromMetadata(nextMetadata));
      setIdentityToken(null);
      setIdentityOtpStatus({ sent: false, smsSent: false, emailSent: false });
      setIsIdentityOtpSending(false);
      setIsIdentityOtpVerifying(false);
      setSigningLink(null);
      setConsentHtml(null);

      const nextType =
        (nextMetadata.household.type as HouseholdType | null) ??
        resolvedHouseholdType;
      setHouseholdType(nextType);
      leadStageRef.current = { leadId: response.leadId, stageId: null };
      void updateLeadStage(
        leadConfig.stagePipelineIntentId ??
        leadConfig.stagePipelineId ??
        null,
        response.leadId
      );
      pushToDataLayer({
        event: "lead_started",
        leadId: response.leadId,
        language,
        ...(nextType ? { householdType: nextType } : {}),
      });
      return nextMetadata;
    } catch (error: any) {
      const fallback = t("wizard.errors.generic");
      const message =
        typeof error?.message === "string" && error.message.trim().length > 0
          ? error.message
          : fallback;
      if (message.toLowerCase().includes("captcha")) {
        setApiError("No pudimos validar el captcha. Intenta nuevamente.");
      } else {
        setApiError(message);
      }
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const leadConfig = useMemo(() => decodePublicTokenConfig(), []);

  const displayPhone = leadConfig.phoneNumber || t("header.phone");
  const displayWhatsApp =
    leadConfig.whatsappNumber || process.env.REACT_APP_WHATSAPP_NUMBER || "";
  const cleanWhatsApp = displayWhatsApp.replace(/\D/g, "");

  const [startLeadMutation] =
    useMutation<PublicStartLeadResponse>(PUBLIC_START_LEAD);
  const [updateHouseholdMutation] = useMutation<PublicUpdateHouseholdResponse>(
    PUBLIC_UPDATE_HOUSEHOLD
  );
  const [confirmPlanMutation] =
    useMutation<PublicConfirmPlanResponse>(PUBLIC_CONFIRM_PLAN);
  const [checkConsentMutation] =
    useMutation<PublicCheckConsentResponse>(PUBLIC_CHECK_CONSENT);
  const [initIdentityMutation] =
    useMutation<PublicInitIdentityResponse>(PUBLIC_INIT_IDENTITY);
  const [verifyIdentityOtpMutation] =
    useMutation<PublicVerifyIdentityOtpResponse>(PUBLIC_VERIFY_IDENTITY_OTP);
  const [createQuoteMutation] =
    useMutation<PublicCreateQuoteResponse>(PUBLIC_CREATE_QUOTE);
  const [updateLeadStageMutation] =
    useMutation<PublicUpdateLeadStageResponse>(PUBLIC_UPDATE_LEAD_STAGE);

  const pageTitle = t(STEP_TITLE_KEYS[wizardStep]);

  const updateLeadStage = useCallback(
    async (nextStageId?: number | null, explicitLeadId?: string | null) => {
      const resolvedStageId =
        typeof nextStageId === "number" && Number.isFinite(nextStageId)
          ? nextStageId
          : null;
      const effectiveLeadId = explicitLeadId ?? leadId;
      if (!effectiveLeadId || !resolvedStageId) {
        return;
      }
      if (
        leadStageRef.current.leadId === effectiveLeadId &&
        leadStageRef.current.stageId === resolvedStageId
      ) {
        return;
      }
      try {
        await updateLeadStageMutation({
          variables: {
            token: publicToken,
            leadId: effectiveLeadId,
            stagePipelineId: resolvedStageId,
          },
        });
        leadStageRef.current = {
          leadId: effectiveLeadId,
          stageId: resolvedStageId,
        };
      } catch (error) {
        console.error("Failed to update lead stage", error);
      }
    },
    [leadId, publicToken, updateLeadStageMutation]
  );

  useEffect(() => {
    pushToDataLayer({
      event: "wizard_step",
      step: wizardStep,
      language,
      ...(leadId ? { leadId } : {}),
    });
  }, [wizardStep, language, leadId]);

  useEffect(() => {
    if (!leadId) {
      consentTrackedLeadRef.current = null;
      return;
    }
    if (
      consentTrackedLeadRef.current &&
      consentTrackedLeadRef.current !== leadId &&
      consentStatus !== "SIGNED"
    ) {
      consentTrackedLeadRef.current = null;
    }
  }, [leadId, consentStatus]);

  useEffect(() => {
    if (consentStatus !== "SIGNED" || !leadId) {
      return;
    }
    if (consentTrackedLeadRef.current === leadId) {
      return;
    }

    const baseHolder = buildQuoteHolderState(metadata, currentPrimary ?? null);
    setQuoteHolderState(baseHolder);
    setQuoteMembersState(buildQuoteMembersState(metadata));
    setQuotePaymentState(buildQuotePaymentState(baseHolder));
    setQuotePolicyId(null);
    setApiError(null);
    if (
      !["quoteHolder", "quoteMembers", "quotePayment", "quoteSuccess"].includes(
        wizardStep
      )
    ) {
      setWizardStep("quoteHolder");
    }

    consentTrackedLeadRef.current = leadId;

    const planId = selectedPlan?.id ?? metadata.plan?.selection?.planId ?? null;

    pushToDataLayer({
      event: "consent_signed",
      leadId,
      language,
      signedAt: new Date().toISOString(),
      ...(planId ? { planId } : {}),
    });
    void updateLeadStage(
      leadConfig.stagePipelineSignedId ??
      leadConfig.stagePipelineId ??
      null
    );
  }, [
    consentStatus,
    leadId,
    selectedPlan,
    metadata,
    language,
    currentPrimary,
    wizardStep,
    leadConfig.stagePipelineSignedId,
    leadConfig.stagePipelineId,
    updateLeadStage,
  ]);

  useEffect(() => {
    if (identityVerified && wizardStep === "identityOtp") {
      setWizardStep("plans");
    }
  }, [identityVerified, wizardStep]);

  const handleToggleLanguage = () => {
    const nextLanguage: Language = language === "es" ? "en" : "es";
    setLanguage(nextLanguage);
  };

  const handleSelectHouseholdType = (type: HouseholdType) => {
    setHouseholdType(type);
    setMetadata((prev) => ({
      ...prev,
      household: { ...prev.household, type },
    }));
    if (type !== undefined) {
      setWizardStep("householdBasics");
    }
  };

  const handleHouseholdBasicsSubmit = ({
    zipCode,
    income,
    countyFips,
    countyName,
    stateId,
    stateName,
    effectiveDate,
  }: {
    zipCode: string;
    income: number;
    countyFips: string;
    countyName?: string | null;
    stateId?: string | null;
    stateName?: string | null;
    effectiveDate?: string | null;
  }) => {
    setMetadata((prev) => ({
      ...prev,
      household: {
        ...prev.household,
        type: resolvedHouseholdType ?? householdType,
        zipCode,
        income,
        countyFips,
        countyName: countyName ?? prev.household.countyName,
        stateId: stateId ?? prev.household.stateId,
        stateName: stateName ?? prev.household.stateName,
        effectiveDate: effectiveDate ?? prev.household.effectiveDate,
      },
    }));
    setWizardStep("primary");
    setApiError(null);
  };

  const handlePrimarySubmit = async (applicant: WizardPrimaryApplicant) => {
    if (!resolvedHouseholdType) {
      return;
    }

    if (
      !leadConfig.campusId ||
      !leadConfig.leadSourceId ||
      !leadConfig.stagePipelineId
    ) {
      setApiError(
        "Missing lead configuration. Configure environment variables for stage pipeline, lead source and campus."
      );
      return;
    }

    const primaryMember = buildPrimaryMember(applicant);
    const existingAdditional = (metadata.members || []).filter(
      (member) => member.role !== "PRIMARY"
    );
    const nextMembers = [primaryMember, ...existingAdditional];

    const householdPayload: LeadHousehold = {
      ...metadata.household,
      type: resolvedHouseholdType,
    };

    setMetadata((prev) => ({
      ...prev,
      members: nextMembers,
      household: {
        ...prev.household,
        type: resolvedHouseholdType,
      },
    }));
    setPrimaryApplicant(applicant);
    setApiError(null);

    if (householdNeedsAdditionalMembers(resolvedHouseholdType) && !leadId) {
      setWizardStep("members");
      return;
    }

    const nextMetadata = await submitStartLead(
      nextMembers,
      applicant,
      householdPayload
    );

    if (!nextMetadata) {
      return;
    }

    const nextType =
      (nextMetadata.household.type as HouseholdType | null) ??
      resolvedHouseholdType;
    const needsMoreMembers = householdNeedsAdditionalMembers(nextType);
    if (needsMoreMembers) {
      setWizardStep("members");
      return;
    }

    const nextIdentityStatus = identityStatusFromMetadata(nextMetadata);
    if (!leadConfig.requireIdentity) {
      setWizardStep("plans");
    } else {
      setWizardStep(nextIdentityStatus === "VERIFIED" ? "plans" : "identityOtp");
    }

    if (nextIdentityStatus !== "VERIFIED") {
      setSigningLink(null);
      setConsentHtml(null);
    }
  };

  const handleMembersSubmit = async (membersList: LeadHouseholdMember[]) => {
    const normalizedMembers = membersList;
    setMetadata((prev) => ({
      ...prev,
      members: normalizedMembers,
    }));

    if (!leadId) {
      if (!primaryApplicant) {
        setApiError(t("wizard.errors.generic"));
        return;
      }

      const householdPayload: LeadHousehold = {
        ...metadata.household,
        type: resolvedHouseholdType,
      };

      const nextMetadata = await submitStartLead(
        normalizedMembers,
        primaryApplicant,
        householdPayload
      );

      if (nextMetadata) {
        const nextIdentityStatus = identityStatusFromMetadata(nextMetadata);
        if (!leadConfig.requireIdentity) {
          setWizardStep("plans");
        } else {
          setWizardStep(
            nextIdentityStatus === "VERIFIED" ? "plans" : "identityOtp"
          );
        }
        if (nextIdentityStatus !== "VERIFIED") {
          setSigningLink(null);
          setConsentHtml(null);
        }
      }
      return;
    }

    setIsSubmitting(true);
    setApiError(null);

    try {
      const { data } = await updateHouseholdMutation({
        variables: {
          token: publicToken,
          leadId,
          members: normalizedMembers.map(mapMemberToInput),
          wizardStep: "PLANS",
        },
      });

      const response = data?.publicUpdateHousehold;
      if (!response?.success) {
        const message =
          response?.errors?.[0]?.message || t("wizard.errors.generic");
        setApiError(message);
        return;
      }

      const nextMetadata = parseMetadata(response.metadata);
      setMetadata(nextMetadata);
      setConsentStatus(consentFromMetadata(nextMetadata));
      const nextIdentityStatus = identityStatusFromMetadata(nextMetadata);
      if (!leadConfig.requireIdentity) {
        setWizardStep("plans");
      } else {
        setWizardStep(
          nextIdentityStatus === "VERIFIED" ? "plans" : "identityOtp"
        );
      }
      if (nextIdentityStatus !== "VERIFIED") {
        setSigningLink(null);
        setConsentHtml(null);
      }
    } catch (error: any) {
      setApiError(error?.message ?? t("wizard.errors.generic"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePlanSelect = (plan: Plan, context: PlanSelectionContext) => {
    if (!isHouseholdComplete(resolvedHouseholdType, metadata.members ?? [])) {
      setApiError(t("wizard.members.errors.completeCouple"));
      setWizardStep("members");
      return;
    }
    if (!identityVerified && leadConfig.requireIdentity) {
      setApiError(t("wizard.errors.identityRequired"));
      setWizardStep("identityOtp");
      return;
    }

    setSelectedPlan(plan);
    setPlanContext(context);
    if (typeof plan.monthlyPremium === "number") {
      setRequiresPayment(plan.monthlyPremium > 0);
    }
    setWizardStep("summary");
  };

  const handleConfirmPlan = async () => {
    if (!leadId || !selectedPlan) {
      return;
    }

    if (!identityVerified && leadConfig.requireIdentity) {
      setApiError(t("wizard.errors.identityRequired"));
      setWizardStep("identityOtp");
      return;
    }

    if (consentStatus !== "NOT_REQUESTED" && signingLink) {
      setWizardStep("summary");
      return;
    }

    setIsSubmitting(true);
    setApiError(null);

    try {
      const { data } = await confirmPlanMutation({
        variables: {
          token: publicToken,
          leadId,
          planSelection: buildPlanSelectionInput(selectedPlan),
          planResults: {
            count: planContext?.count ?? 0,
            fetchedAt: planContext?.fetchedAt,
            filters: JSON.stringify({
              language,
              year: planContext?.year ?? undefined,
            }),
          },
          signatureFormId:
            language === "es"
              ? leadConfig.signatureFormId
              : leadConfig.signatureFormIdEn,
          agentId: leadConfig.agentId,
          sendEmail: leadConfig.sendEmail,
          sendSms: leadConfig.sendSms,
          getSigningLink: true,
        },
      });

      const response = data?.publicConfirmPlan;
      if (!response?.success) {
        const message =
          response?.errors?.[0]?.message || t("wizard.errors.generic");
        setApiError(message);
        return;
      }

      const nextMetadata = parseMetadata(response.metadata);
      setMetadata(nextMetadata);
      const nextConsentStatus = consentFromMetadata(nextMetadata);
      setConsentStatus(nextConsentStatus);
      setSigningLink(response.signingLink ?? null);
      setConsentHtml(response.consentHtml ?? null);
      setWizardStep(nextConsentStatus === "SIGNED" ? "consent" : "summary");
    } catch (error: any) {
      setApiError(error?.message ?? t("wizard.errors.generic"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckConsent = useCallback(
    async (silent = false) => {
      if (!leadId) {
        return;
      }
      if (!silent) {
        setIsCheckingConsent(true);
        setApiError(null);
      }

      try {
        const { data } = await checkConsentMutation({
          variables: {
            token: publicToken,
            leadId,
          },
        });

        const response = data?.publicCheckConsent;
        if (!response?.success) {
          if (!silent) {
            const message =
              response?.errors?.[0]?.message || t("wizard.errors.generic");
            setApiError(message);
          }
          return;
        }

        const nextMetadata = parseMetadata(response.metadata);
        setMetadata(nextMetadata);
        const nextStatus = consentFromMetadata(nextMetadata);
        setConsentStatus(nextStatus);

        if (nextStatus === "SIGNED") {
          setWizardStep("consent");
        }
      } catch (error: any) {
        if (!silent) {
          setApiError(error?.message ?? t("wizard.errors.generic"));
        }
      } finally {
        if (!silent) {
          setIsCheckingConsent(false);
        }
      }
    },
    [checkConsentMutation, leadId, publicToken, t]
  );

  useEffect(() => {
    if (consentStatus !== "PENDING" || !signingLink || !leadId) {
      return;
    }

    void handleCheckConsent(true);
    const interval = setInterval(() => {
      void handleCheckConsent(true);
    }, 7000);

    return () => clearInterval(interval);
  }, [consentStatus, signingLink, leadId, handleCheckConsent]);

  const handleRequestIdentityOtp = async (channels: {
    sendSms: boolean;
    sendEmail: boolean;
  }) => {
    if (!leadId) {
      throw new Error(t("wizard.errors.generic"));
    }

    setIsIdentityOtpSending(true);
    setApiError(null);

    try {
      const { data } = await initIdentityMutation({
        variables: {
          token: publicToken,
          leadId,
          sendSms: channels.sendSms,
          sendEmail: channels.sendEmail,
          language,
        },
      });

      const response = data?.publicInitIdentity;
      if (!response?.success || !response.identityToken) {
        const message =
          response?.errors?.[0]?.message || t("wizard.errors.generic");
        setApiError(message);
        throw new Error(message);
      }

      setIdentityToken(response.identityToken);
      setIdentityOtpStatus({
        sent: true,
        smsSent: Boolean(response.smsSent),
        emailSent: Boolean(response.emailSent),
      });

      if (response.metadata) {
        setMetadata(parseMetadata(response.metadata));
      }

      pushToDataLayer({
        event: "identity_otp_sent",
        leadId,
        language,
        smsSent: Boolean(response.smsSent),
        emailSent: Boolean(response.emailSent),
      });
    } catch (error: any) {
      const message = error?.message ?? t("wizard.errors.generic");
      setApiError(message);
      throw new Error(message);
    } finally {
      setIsIdentityOtpSending(false);
    }
  };

  const handleVerifyIdentityOtp = async (code: string) => {
    if (!leadId || !identityToken) {
      throw new Error(t("identity.otp.codeRequired"));
    }

    setIsIdentityOtpVerifying(true);
    setApiError(null);

    try {
      const { data } = await verifyIdentityOtpMutation({
        variables: {
          token: publicToken,
          leadId,
          identityToken,
          otpCode: code,
        },
      });

      const response = data?.publicVerifyIdentityOtp;
      if (!response?.success) {
        const message =
          response?.errors?.[0]?.message || t("wizard.errors.generic");
        setApiError(message);
        throw new Error(message);
      }

      if (response.metadata) {
        const nextMetadata = parseMetadata(response.metadata);
        setMetadata(nextMetadata);
        setConsentStatus(consentFromMetadata(nextMetadata));
      }

      setIdentityToken(null);
      setIdentityOtpStatus((prev) => ({ ...prev, sent: true }));
      setSigningLink(null);
      setConsentHtml(null);
      setWizardStep("plans");

      pushToDataLayer({
        event: "identity_verified",
        leadId,
        language,
      });
    } catch (error: any) {
      const message = error?.message ?? t("wizard.errors.generic");
      setApiError(message);
      throw new Error(message);
    } finally {
      setIsIdentityOtpVerifying(false);
    }
  };

  const handleQuoteHolderSubmit = async (values: QuoteHolderFormState) => {
    setApiError(null);
    setQuoteHolderState(values);
    setQuotePaymentState((prev) => ({
      ...prev,
      paymentHolder: `${values.firstName} ${values.lastName}`.trim(),
      billingAddress1: values.address1,
      billingAddress2: values.address2,
      billingCity: values.city,
      billingStateCode: values.stateCode,
      billingZipCode: values.zipCode,
    }));

    if (quoteMembersState.length > 0) {
      setWizardStep("quoteMembers");
      return;
    }

    if (requiresPayment) {
      setWizardStep("quotePayment");
      return;
    }

    const noPaymentValues = buildNoPaymentState(values);
    setQuotePaymentState(noPaymentValues);

    try {
      await handleQuotePaymentSubmit(noPaymentValues, quoteMembersState);
    } catch (error: any) {
      setApiError(error?.message ?? t("wizard.errors.generic"));
    }
  };

  const handleQuoteMembersSubmit = async (
    membersValues: QuoteMemberFormState[]
  ) => {
    setApiError(null);
    setQuoteMembersState(membersValues);
    if (requiresPayment) {
      setWizardStep("quotePayment");
      return;
    }

    const noPaymentValues = buildNoPaymentState(quoteHolderState);
    setQuotePaymentState(noPaymentValues);

    try {
      await handleQuotePaymentSubmit(noPaymentValues, membersValues);
    } catch (error: any) {
      setApiError(error?.message ?? t("wizard.errors.generic"));
    }
  };

  const handleQuotePaymentSubmit = async (
    values: QuotePaymentFormState,
    membersOverride?: QuoteMemberFormState[]
  ) => {
    if (!leadId) {
      setApiError(t("wizard.errors.generic"));
      return;
    }

    setQuotePaymentState(values);
    setIsCreatingQuote(true);
    setApiError(null);

    try {
      const holderPayload = {
        firstName: quoteHolderState.firstName,
        lastName: quoteHolderState.lastName,
        gender: quoteHolderState.gender,
        birthDate: quoteHolderState.birthDate,
        email: quoteHolderState.email,
        phone: quoteHolderState.phone,
        address1: quoteHolderState.address1,
        address2: quoteHolderState.address2,
        city: quoteHolderState.city,
        stateCode: quoteHolderState.stateCode,
        stateName: quoteHolderState.stateName ?? "",
        zipCode: quoteHolderState.zipCode,
        countyFips: quoteHolderState.countyFips ?? "",
        ssn: quoteHolderState.ssn,
        usesTobacco: Boolean(quoteHolderState.usesTobacco),
        isPregnant: Boolean(quoteHolderState.isPregnant),
      };

      const membersSource = membersOverride ?? quoteMembersState;
      const membersPayload = membersSource.map((member) => ({
        firstName: member.firstName,
        lastName: member.lastName,
        gender: member.gender,
        birthDate: member.birthDate,
        relationship: member.relationship,
        email: member.email ?? "",
        phone: member.phone ?? "",
        usesTobacco: Boolean(member.usesTobacco),
        isPregnant: Boolean(member.isPregnant),
      }));

      const paymentMethod = values.providePaymentLater ? "NONE" : values.method;
      const paymentPayload = {
        method: paymentMethod,
        cardNumber:
          paymentMethod === "CARD" ? values.cardNumber.replace(/\D/g, "") : "",
        expirationMonth: paymentMethod === "CARD" ? values.expirationMonth : "",
        expirationYear: paymentMethod === "CARD" ? values.expirationYear : "",
        cvv: paymentMethod === "CARD" ? values.cvv : "",
        cardType: paymentMethod === "CARD" ? values.cardType : "",
        cardCompany: paymentMethod === "CARD" ? values.cardCompany : "",
        bankName: paymentMethod === "BANK" ? values.bankName : "",
        abaRouting: paymentMethod === "BANK" ? values.abaRouting : "",
        accountNumber: paymentMethod === "BANK" ? values.accountNumber : "",
        paymentHolder: values.paymentHolder,
        billingAddress1: values.billingAddress1,
        billingAddress2: values.billingAddress2,
        billingCity: values.billingCity,
        billingStateCode: values.billingStateCode,
        billingZipCode: values.billingZipCode,
      };

      const { data } = await createQuoteMutation({
        variables: {
          token: publicToken,
          leadId,
          quoteToken: null,
          holder: holderPayload,
          members: membersPayload,
          payment: paymentPayload,
        },
      });

      const response = data?.publicCreateQuote;
      if (!response?.success || !response.policyId) {
        const message =
          response?.errors?.[0]?.message || t("wizard.errors.generic");
        setApiError(message);
        return;
      }

      setQuotePolicyId(response.policyId);
      if (response.metadata) {
        setMetadata(parseMetadata(response.metadata));
      }
      setWizardStep("quoteSuccess");
      pushToDataLayer({
        event: "quote_created",
        leadId,
        language,
        policyId: response.policyId,
      });
      void updateLeadStage(
        leadConfig.stagePipelineQuotedId ??
        leadConfig.stagePipelineId ??
        null
      );
    } catch (error: any) {
      const message = error?.message ?? t("wizard.errors.generic");
      setApiError(message);
    } finally {
      setIsCreatingQuote(false);
    }
  };

  const handleQuoteFinish = useCallback(() => {
    pushToDataLayer({
      event: "quote_success_finish",
      language,
      ...(leadId ? { leadId } : {}),
      ...(quotePolicyId ? { policyId: quotePolicyId } : {}),
      ...(successPlanName ? { planName: successPlanName } : {}),
      ...(successPlanCarrier ? { planCarrier: successPlanCarrier } : {}),
    });
    window.location.reload();
  }, [language, leadId, quotePolicyId, successPlanName, successPlanCarrier]);

  const nextLanguage: Language = language === "es" ? "en" : "es";
  const toggleLabel = t("header.languageToggleLabel", {
    language: t(`header.options.${nextLanguage}`),
  });

  const wizardSteps = useMemo<WizardStep[]>(() => {
    const steps: WizardStep[] = ["householdType", "householdBasics", "primary"];

    if (householdNeedsAdditionalMembers(resolvedHouseholdType)) {
      steps.push("members");
    }

    if (leadConfig.requireIdentity) {
      steps.push("identityOtp");
    }

    steps.push("plans", "summary", "consent", "quoteHolder");

    if (quoteMembersState.length > 0) {
      steps.push("quoteMembers");
    }

    if (requiresPayment) {
      steps.push("quotePayment");
    }

    steps.push("quoteSuccess");

    return steps;
  }, [resolvedHouseholdType, quoteMembersState.length, requiresPayment]);

  const currentStepIndex = wizardSteps.indexOf(wizardStep);
  const progress =
    currentStepIndex >= 0
      ? ((currentStepIndex + 1) / wizardSteps.length) * 100
      : 0;

  const selectedHouseholdTypeIcon = useMemo(() => {
    const match = OPTIONS.find((option) => option.type === householdType);
    return match?.icon ?? null;
  }, [householdType]);

  // Wizard phases para el stepper visual (agrupadas)
  const WIZARD_PHASES = [
    { key: "household", steps: ["householdType", "householdBasics"] as WizardStep[], label: t("steps.household") },
    { key: "primary", steps: ["primary", "members"] as WizardStep[], label: t("wizard.titles.primary") },
    { key: "identity", steps: ["identityOtp"] as WizardStep[], label: t("wizard.titles.identityOtp") },
    { key: "plans", steps: ["plans", "summary", "consent"] as WizardStep[], label: t("steps.plans") },
    { key: "quote", steps: ["quoteHolder", "quoteMembers", "quotePayment", "quoteSuccess"] as WizardStep[], label: t("steps.details") },
  ];

  const visiblePhases = WIZARD_PHASES.filter(phase =>
    phase.steps.some(s => wizardSteps.includes(s))
  );

  const currentPhaseIndex = visiblePhases.findIndex(phase =>
    phase.steps.includes(wizardStep)
  );

  const stepperFillPct = visiblePhases.length > 1
    ? (currentPhaseIndex / (visiblePhases.length - 1)) * 100
    : 0;

  const handleScrollToWizard = useCallback(() => {
    const wizardTarget =
      document.querySelector("#quote-wizard") ||
      document.querySelector(".wizard-progress");
    if (wizardTarget) {
      wizardTarget.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const socialCards =
    language === "es"
      ? [
        {
          id: "views",
          icon: <svg className="social-icon green" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>,
          boldText: "75 personas",
          regularText: "buscando ahora"
        },
        {
          id: "families",
          icon: <svg className="social-icon blue" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>,
          boldText: "+1M",
          regularText: "familias ayudadas"
        },
        {
          id: "recent",
          icon: <svg className="social-icon orange" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>,
          boldText: RECENT_CITIES[currentCityIndex],
          regularText: "Reciente:",
          reverse: true
        },
      ]
      : [
        {
          id: "views",
          icon: <svg className="social-icon green" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>,
          boldText: "75 people",
          regularText: "searching now"
        },
        {
          id: "families",
          icon: <svg className="social-icon blue" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>,
          boldText: "+1M",
          regularText: "families helped"
        },
        {
          id: "recent",
          icon: <svg className="social-icon orange" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>,
          boldText: RECENT_CITIES[currentCityIndex],
          regularText: "Recent:",
          reverse: true
        },
      ];
  const riskCards =
    language === "es"
      ? [
        {
          title: "Emergencia Medica",
          text: "Sin seguro, una visita a emergencias puede costarte miles de dolares.",
        },
        {
          title: "Costos Inesperados",
          text: "Un procedimiento medico sin cobertura puede generar facturas enormes.",
        },
        {
          title: "Deudas Medicas",
          text: "Las deudas medicas pueden afectar tu historial y desestabilizar tu hogar.",
        },
      ]
      : [
        {
          title: "Medical Emergency",
          text: "Without insurance, an ER visit can cost thousands of dollars.",
        },
        {
          title: "Unexpected Costs",
          text: "A medical procedure without coverage can generate very large bills.",
        },
        {
          title: "Medical Debt",
          text: "Medical debt can damage your credit and household stability.",
        },
      ];
  const premiumBenefits =
    language === "es"
      ? [
        { title: "Comenzar toma menos de 5 minutos", text: "Proceso rápido y sencillo" },
        { title: "Nuestros asesores expertos te ayudarán", text: "A elegir la cobertura correcta" },
        { title: "Compara planes para encontrar", text: "Las tarifas más bajas disponibles" },
      ]
      : [
        { title: "Getting started takes less than 5 minutes", text: "Quick and simple process" },
        { title: "Our expert advisors will help you", text: "To choose the right coverage" },
        { title: "Compare plans to find", text: "The lowest available rates" },
      ];

  const fplTableHeaders =
    language === "es"
      ? ["Tamaño del Hogar", "100% FPL", "150% FPL", "200% FPL ★", "400% FPL"]
      : ["Household Size", "100% FPL", "150% FPL", "200% FPL ★", "400% FPL"];

  const fplTableRows =
    language === "es"
      ? [
        { size: "1 persona", fpl100: "$15,060", fpl150: "$22,590", fpl200: "$30,120", fpl400: "$60,240" },
        { size: "2 personas", fpl100: "$20,440", fpl150: "$30,660", fpl200: "$40,880", fpl400: "$81,760" },
        { size: "3 personas", fpl100: "$25,820", fpl150: "$38,730", fpl200: "$51,640", fpl400: "$103,280" },
        { size: "4 personas", fpl100: "$31,200", fpl150: "$46,800", fpl200: "$62,400", fpl400: "$124,800" },
        { size: "5 personas", fpl100: "$36,580", fpl150: "$54,870", fpl200: "$73,160", fpl400: "$146,320" },
        { size: "6 personas", fpl100: "$41,960", fpl150: "$62,940", fpl200: "$83,920", fpl400: "$167,840" },
      ]
      : [
        { size: "1 person", fpl100: "$15,060", fpl150: "$22,590", fpl200: "$30,120", fpl400: "$60,240" },
        { size: "2 people", fpl100: "$20,440", fpl150: "$30,660", fpl200: "$40,880", fpl400: "$81,760" },
        { size: "3 people", fpl100: "$25,820", fpl150: "$38,730", fpl200: "$51,640", fpl400: "$103,280" },
        { size: "4 people", fpl100: "$31,200", fpl150: "$46,800", fpl200: "$62,400", fpl400: "$124,800" },
        { size: "5 people", fpl100: "$36,580", fpl150: "$54,870", fpl200: "$73,160", fpl400: "$146,320" },
        { size: "6 people", fpl100: "$41,960", fpl150: "$62,940", fpl200: "$83,920", fpl400: "$167,840" },
      ];

  const detailBenefits =
    language === "es"
      ? [
        "Servicios de emergencia",
        "Servicios para pacientes ambulatorios",
        "Hospitalizacion",
        "Cuidado antes y despues que nazca su bebe",
        "Servicios para la salud mental y abuso de sustancias",
        "Sus medicamentos recetados",
        "Fisioterapia, terapia ocupacional y rehabilitacion",
        "Servicios de laboratorios",
        "Servicios preventivos: examenes, vacunas y consejeria",
        "Servicios pediatricos: cuidado dental y de la vista",
      ]
      : [
        "Emergency services",
        "Outpatient services",
        "Hospitalization",
        "Maternity and newborn care",
        "Mental health and substance abuse services",
        "Prescription medications",
        "Physical therapy, occupational therapy and rehabilitation",
        "Laboratory services",
        "Preventive services: screenings, vaccines and counseling",
        "Pediatric services: dental and vision care",
      ];
  const metalTiers =
    language === "es"
      ? [
        { id: "bronze", name: "Bronce", planPays: "60%", youPay: "40%" },
        { id: "silver", name: "Plata", planPays: "70%", youPay: "30%" },
        { id: "gold", name: "Oro", planPays: "80%", youPay: "20%" },
        { id: "platinum", name: "Platino", planPays: "90%", youPay: "10%" },
      ]
      : [
        { id: "bronze", name: "Bronze", planPays: "60%", youPay: "40%" },
        { id: "silver", name: "Silver", planPays: "70%", youPay: "30%" },
        { id: "gold", name: "Gold", planPays: "80%", youPay: "20%" },
        { id: "platinum", name: "Platinum", planPays: "90%", youPay: "10%" },
      ];
  const testimonials =
    language === "es"
      ? [
        {
          img: "/media/testimonial-maria.jpg",
          quote:
            "Estoy encantada con el servicio. Mi agente me encontro un plan increible con $0 de prima mensual.",
          name: "Maria G.",
          city: "Miami, FL",
        },
        {
          img: "/media/testimonial-carlos.jpg",
          quote:
            "El proceso fue super facil. En menos de 10 minutos ya tenia mi plan de salud para toda la familia.",
          name: "Carlos R.",
          city: "Houston, TX",
        },
        {
          img: "/media/testimonial-rosa.jpg",
          quote:
            "Me ayudaron a conseguir cobertura completa por muy poco. Gracias por la paciencia y claridad.",
          name: "Rosa M.",
          city: "Los Angeles, CA",
        },
      ]
      : [
        {
          img: "/media/testimonial-maria.jpg",
          quote:
            "I was thrilled with the service. My agent found me an amazing plan with a $0 monthly premium.",
          name: "Maria G.",
          city: "Miami, FL",
        },
        {
          img: "/media/testimonial-carlos.jpg",
          quote:
            "The process was super easy. In less than 10 minutes I had a health plan for the whole family.",
          name: "Carlos R.",
          city: "Houston, TX",
        },
        {
          img: "/media/testimonial-rosa.jpg",
          quote:
            "They helped me get full coverage for very little. Thanks for the patience and clarity.",
          name: "Rosa M.",
          city: "Los Angeles, CA",
        },
      ];
  const stepsContent =
    language === "es"
      ? [
        {
          number: "1",
          title: "Cuentanos sobre ti",
          text: "Completa el formulario con tu codigo postal e informacion basica.",
        },
        {
          number: "2",
          title: "Comparamos planes",
          text: "Un agente licenciado analiza las mejores opciones segun tus necesidades e ingresos.",
        },
        {
          number: "3",
          title: "Queda protegido",
          text: "Selecciona tu plan ideal y completa la inscripcion rapido y sin costo.",
        },
      ]
      : [
        {
          number: "1",
          title: "Tell us about yourself",
          text: "Complete the form with your ZIP code and basic information.",
        },
        {
          number: "2",
          title: "We compare plans",
          text: "A licensed agent reviews the best options based on your needs and income.",
        },
        {
          number: "3",
          title: "Get protected",
          text: "Choose your ideal plan and complete enrollment quickly and at no cost.",
        },
      ];
  const faqItems =
    language === "es"
      ? [
        {
          q: "¿Cuánto cuesta inscribirse?",
          a: "La inscripción es 100% gratuita. Solo pagarás la prima mensual del plan que elijas. Muchas personas califican para subsidios que reducen el costo a $0/mes.",
        },
        {
          q: "¿Puedo mantener a mi médico actual?",
          a: "Depende del plan que elijas. Cada plan tiene su propia red de proveedores. Nuestros agentes te ayudan a verificar que tu médico esté incluido antes de inscribirte.",
        },
        {
          q: "¿Califico para subsidios o ayuda financiera?",
          a: "Depende de tus ingresos y tamaño de familia. Nuestros agentes verifican automáticamente si calificas durante la llamada. La mayoría de nuestros clientes reciben algún tipo de ayuda.",
        },
        {
          q: "¿Qué documentos necesito?",
          a: "Necesitarás tu número de Seguro Social, información de ingresos (W-2 o declaración de impuestos) e información de los miembros de tu familia que deseas cubrir.",
        },
        {
          q: "¿Cuándo es el período de inscripción?",
          a: "La inscripción abierta es generalmente del 1 de noviembre al 15 de enero. Si tienes un evento de vida calificador (pérdida de empleo, matrimonio, nacimiento), puedes inscribirte en cualquier momento.",
        },
        {
          q: "¿Necesito ser ciudadano para inscribirme?",
          a: "No necesitas ser ciudadano. Residentes permanentes legales y personas con ciertos estatus migratorios también pueden calificar para planes del Mercado de Salud.",
        },
      ]
      : [
        {
          q: "How much does it cost to enroll?",
          a: "Enrollment is 100% free. You'll only pay the monthly premium of the plan you choose. Many people qualify for subsidies that lower the cost to $0/month.",
        },
        {
          q: "Can I keep my current doctor?",
          a: "It depends on the plan you choose. Each plan has its own provider network. Our agents help verify that your doctor is included before enrolling.",
        },
        {
          q: "Do I qualify for subsidies or financial help?",
          a: "It depends on your income and household size. Our agents automatically check if you qualify during the call. Most of our clients receive some form of assistance.",
        },
        {
          q: "What documents do I need?",
          a: "You'll need your Social Security number, income information (W-2 or tax return), and information for the household members you want to cover.",
        },
        {
          q: "When is the enrollment period?",
          a: "Open enrollment is generally from November 1 to January 15. If you have a qualifying life event (job loss, marriage, birth), you can enroll at any time.",
        },
        {
          q: "Do I need to be a citizen to enroll?",
          a: "You do not need to be a citizen. Lawful permanent residents and individuals with certain immigration statuses can also qualify for Marketplace health plans.",
        },
      ];

  const logos = ["aetna", "ambetter", "MOLINA HEALTHCARE", "United Healthcare", "Cigna", "Oscar"];
  const HeroInlineCard = ({ language, householdType, onSelectHousehold, phone }: any) => {
    const options = language === 'es'
      ? [{ label: 'Soltero/a', icon: '👤', value: 'SOLTERO' }, { label: 'Pareja', icon: '👥', value: 'PAREJA' }, { label: 'Familia', icon: '👨‍👩‍👧', value: 'FAMILIA' }]
      : [{ label: 'Single', icon: '👤', value: 'SOLTERO' }, { label: 'Couple', icon: '👥', value: 'PAREJA' }, { label: 'Family', icon: '👨‍👩‍👧', value: 'FAMILIA' }];
    const handleSelect = (value: string) => {
      onSelectHousehold(value as any);
      setTimeout(() => { const el = document.getElementById('quote-wizard'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100);
    };
    return (
      <section style={{ borderRadius: '1rem', background: 'var(--card)', padding: '32px', boxShadow: 'var(--shadow-form)', maxWidth: '430px', width: '100%' }} aria-label="Formulario de cotización">
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ margin: '0 0 12px', fontSize: '1.5rem', fontWeight: 900, lineHeight: 1.2, color: 'var(--navy)' }}>
            {language === 'es' ? <>¿Calificas para un plan desde <span style={{ color: 'var(--success)' }}>$0 al mes?</span></> : <>Do you qualify for a plan from <span style={{ color: 'var(--success)' }}>$0/month?</span></>}
          </h2>
          <p style={{ margin: '0 0 24px', fontWeight: 500, color: 'var(--navy)' }}>{language === 'es' ? 'Responde en 1 minuto y te llamamos.' : 'Answer in 1 minute and we\'ll call you.'}</p>
        </div>
        <p style={{ margin: '0 0 12px', fontWeight: 900, color: 'var(--navy)' }}>{language === 'es' ? 'Tipo de hogar' : 'Household type'}</p>
        <div style={{ display: 'grid', gap: '12px' }}>
          {options.map(opt => (<button key={opt.value} onClick={() => handleSelect(opt.value)} style={{ borderRadius: '0.75rem', border: `2px solid ${householdType === opt.value ? 'var(--success)' : 'var(--secondary)'}`, background: householdType === opt.value ? 'var(--success-soft)' : 'var(--background)', padding: '16px', textAlign: 'left', fontWeight: 900, color: 'var(--navy)', cursor: 'pointer', transition: 'all 0.2s', fontSize: '1rem' }}><span style={{ marginRight: '12px' }}>{opt.icon}</span>{opt.label}</button>))}
        </div>
        <a href={`tel:${displayPhone}`} style={{ width: '100%', marginTop: '24px', padding: '14px 20px', borderRadius: '999px', background: 'var(--success)', color: 'white', textDecoration: 'none', border: 'none', fontSize: '1rem', fontWeight: 900, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
          <span style={{ fontSize: '1.5rem' }}>☎</span>
          {language === 'es' ? 'LLÁMAME AHORA' : 'CALL ME NOW'}
        </a>
        <p style={{ margin: '20px 0 0', textAlign: 'center', fontSize: '0.875rem', fontWeight: 500, color: 'var(--muted-foreground)' }}>🔒 {language === 'es' ? 'Tu información está 100% segura.' : 'Your information is 100% secure.'}</p>
      </section>
    );
  };

  return (
    <div className="app-shell">
      {/* Header sticky */}
      <header style={{ position: 'sticky', top: 0, zIndex: 40, borderBottom: '1px solid var(--secondary)', background: 'color-mix(in oklab, var(--background) 96%, transparent)', backdropFilter: 'blur(12px)', boxShadow: 'var(--shadow-soft)' }}>
        <div style={{ maxWidth: '80rem', margin: '0 auto', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
          <a href="#inicio" style={{ display: 'flex', alignItems: 'center' }}>
            <img src="/media/logo-asegura.png" alt="Seguros de Salud en Español" style={{ height: '56px', width: 'auto' }} />
          </a>
          <nav style={{ gap: '32px', fontSize: '1rem', fontWeight: 700, color: 'var(--navy)' }} className="hub-nav-desktop">
            {/* <a href="#inicio" style={{ borderBottom: '3px solid var(--success)', paddingBottom: '4px' }}>{language === 'es' ? 'Inicio' : 'Home'}</a>
            <a href="#planes" style={{ transition: 'color 0.2s' }}>{language === 'es' ? 'Planes' : 'Plans'}</a>
            <a href="#elegibilidad" style={{ transition: 'color 0.2s' }}>{language === 'es' ? '¿Cómo funciona?' : 'How it works'}</a> */}
            <a href="#faq" style={{ transition: 'color 0.2s' }}>{language === 'es' ? 'Preguntas frecuentes' : 'FAQ'}</a>
          </nav>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <a href={`tel:${displayPhone}`} style={{ display: 'flex', alignItems: 'center', gap: '12px', color: 'var(--navy)', textDecoration: 'none' }} className="hub-phone-desktop">
              <span className="hub-phone-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--success)', color: 'white', width: '48px', height: '48px', borderRadius: '50%', fontWeight: 900, fontSize: '1.5rem', flexShrink: 0 }}><FontAwesomeIcon icon={faPhone} /></span>
              <span className="hub-phone-text">
                <span className="hub-phone-label" style={{ display: 'block', fontSize: '0.75rem', fontWeight: 900, color: 'var(--success)' }}>{language === 'es' ? 'Llama ahora' : 'Call now'}</span>
                <span className="hub-phone-number" style={{ display: 'block', fontSize: '1.5rem', fontWeight: 900, lineHeight: 1.2, whiteSpace: 'nowrap' }}>{displayPhone}</span>
                <span className="hub-phone-support" style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500 }}>{language === 'es' ? 'Atención en Español' : 'Spanish support'}</span>
              </span>
            </a>
            {/* <button type="button" onClick={handleToggleLanguage} className="language-toggle" aria-label={toggleLabel} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '0.5rem', border: '1px solid var(--secondary)', padding: '8px 12px', gap: '8px', background: 'var(--card)', cursor: 'pointer', transition: 'all 0.2s', fontWeight: 500, color: 'var(--navy)', fontSize: '14px' }}>
              <img src={`/media/flags/${nextLanguage}.png`} alt={nextLanguage.toUpperCase()} className="language-flag" style={{ width: '22px', height: 'auto', borderRadius: '2px' }} />
              <span>{nextLanguage.toUpperCase()}</span>
            </button> */}
          </div>
        </div>
      </header>

      {/* Hero section with background image */}
      <section id="inicio" style={{ position: 'relative', overflow: 'hidden', background: 'var(--hero-surface)', minHeight: '690px' }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.8 }}>
          <img src="/media/hero-family.jpg" alt="Familia con seguro médico" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, var(--background) 0%, color-mix(in oklab,var(--background) 90%,transparent) 31%, color-mix(in oklab,var(--background) 42%,transparent) 58%, color-mix(in oklab,var(--background) 76%,transparent) 100%)' }} />
        <div className="hub-hero-grid">
          <div style={{ maxWidth: '42rem', padding: '40px 0', position: 'relative', zIndex: 1 }}>
            <h1 className="hub-hero-h1">
              {language === 'es' ? <>Seguro médico desde <span style={{ color: 'var(--success)' }}>$0 al mes</span></> : <>Health insurance from <span style={{ color: 'var(--success)' }}>$0/month</span></>}
            </h1>
            <p className="hub-hero-subtitle">
              {language === 'es' ? 'Asesoría en español para encontrar el plan de salud ideal para ti y tu familia.' : 'Spanish-language support to find the best health plan for you and your family.'}
            </p>
            <div style={{ display: 'grid', gap: '20px', marginTop: '36px' }}>
              {(language === 'es'
                ? [['🛡️', 'Planes desde $0 al mes', 'Califica hoy mismo'], ['💬', 'Asesoría en español', 'Gratis y personalizada'], ['☎', 'Llama ahora', 'Sin compromiso']]
                : [['🛡️', 'Plans from $0/month', 'Qualify today'], ['💬', 'Spanish support', 'Free and personalized'], ['☎', 'Call now', 'No commitment']]
              ).map(([icon, title, text]) => (
                <div key={title} style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <span style={{ display: 'grid', placeItems: 'center', width: '48px', height: '48px', borderRadius: '0.75rem', background: 'var(--success)', fontSize: '1.5rem', color: 'var(--success-foreground)', flexShrink: 0, boxShadow: 'var(--shadow-soft)' }}>{icon}</span>
                  <span>
                    <span style={{ display: 'block', fontSize: '1.25rem', fontWeight: 900, color: 'var(--navy)' }}>{title}</span>
                    <span style={{ display: 'block', fontSize: '0.9rem', fontWeight: 500, color: 'var(--navy)' }}>{text}</span>
                  </span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '36px', maxWidth: '42rem', borderRadius: '1rem', background: 'color-mix(in oklab, var(--card) 96%, transparent)', padding: '20px', boxShadow: 'var(--shadow-form)', backdropFilter: 'blur(4px)' }}>
              <p style={{ margin: '0 0 16px', textAlign: 'center', fontWeight: 900, color: 'var(--navy)' }}>
                {language === 'es' ? 'Aceptamos los principales planes' : 'We accept all major plans'}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', textAlign: 'center', alignItems: 'center' }}>
                {[{ src: '/media/aetna.png', alt: 'Aetna' }, { src: '/media/ambetter.png', alt: 'Ambetter' }, { src: '/media/molinahealthcare.jpg', alt: 'Molina' }, { src: '/media/UnitedHealthcare.png', alt: 'UnitedHealthcare' }].map(logo => (
                  <div key={logo.alt} style={{ borderRadius: '0.75rem', background: 'var(--background)', padding: '12px', boxShadow: 'var(--shadow-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60px' }}>
                    <img src={logo.src} alt={logo.alt} style={{ maxWidth: '100%', maxHeight: '50px', width: 'auto', height: 'auto' }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <HeroInlineCard language={language} householdType={householdType} onSelectHousehold={handleSelectHouseholdType} phone={t("header.phone")} />
        </div>
      </section>

      {/* Navy benefits bar */}
      <section style={{ background: 'var(--navy)', padding: '24px 0', color: 'white' }}>
        <div className="hub-container">
          <div className="hub-benefits-bar">
            {(language === 'es'
              ? [['👤', 'Atención 100% en español', 'Te entendemos, te ayudamos'], ['🕒', 'Proceso rápido y fácil', 'Sin papeleo complicado'], ['$', 'Planes económicos', 'Opciones que se adaptan a ti'], ['🛡️', 'Confianza y seguridad', 'Tu salud es nuestra prioridad']]
              : [['👤', '100% Spanish support', 'We understand you'], ['🕒', 'Fast & easy process', 'No complicated paperwork'], ['$', 'Affordable plans', 'Options that fit you'], ['🛡️', 'Trust & security', 'Your health is our priority']]
            ).map(([icon, title, text]) => (
              <article key={title} style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <span style={{ display: 'grid', placeItems: 'center', width: '48px', height: '48px', borderRadius: '50%', background: 'var(--success)', fontSize: '1.5rem', fontWeight: 900, flexShrink: 0 }}>{icon}</span>
                <span>
                  <span style={{ display: 'block', fontWeight: 900 }}>{title}</span>
                  <span style={{ display: 'block', fontSize: '0.875rem', opacity: 0.82 }}>{text}</span>
                </span>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Logo marquee */}
      {/* <section style={{ borderTop:'1px solid var(--secondary)', borderBottom:'1px solid var(--secondary)', background:'var(--card)', padding:'20px 0', overflow:'hidden' }}>
        <div style={{ display:'flex', width:'max-content', gap:'24px', animation:'logo-marquee 40s linear infinite' }} className="logo-marquee">
          {[{src:'/media/cigna.png',alt:'Cigna'},{src:'/media/aetna.png',alt:'Aetna'},{src:'/media/ambetter.png',alt:'Ambetter'},{src:'/media/molinahealthcare.jpg',alt:'Molina'},{src:'/media/UnitedHealthcare.png',alt:'United'},{src:'/media/oscar.jpg',alt:'Oscar'},{src:'/media/bluecross.png',alt:'BlueCross'},{src:'/media/humana.png',alt:'Humana'},{src:'/media/cigna.png',alt:'Cigna'},{src:'/media/aetna.png',alt:'Aetna'},{src:'/media/ambetter.png',alt:'Ambetter'},{src:'/media/molinahealthcare.jpg',alt:'Molina'},{src:'/media/UnitedHealthcare.png',alt:'United'},{src:'/media/oscar.jpg',alt:'Oscar'},{src:'/media/bluecross.png',alt:'BlueCross'},{src:'/media/humana.png',alt:'Humana'}].map((logo, i) => (
            <div key={i} style={{ display:'grid', placeItems:'center', height:'80px', minWidth:'200px', borderRadius:'1rem', border:'1px solid var(--secondary)', background:'var(--secondary)', padding:'0 24px' }}>
              <img src={logo.src} alt={logo.alt} style={{ maxWidth:'100%', maxHeight:'70px', width:'auto', height:'auto' }} />
            </div>
          ))}
        </div>
      </section> */}

      {/* Testimonios */}
      <section className="health-section muted">
        <div className="health-container">
          <div className="health-section-header">
            <h2 className="hub-section-h2" style={{ textAlign: 'center', marginBottom: '48px' }}>{language === 'es' ? 'Familias que ya encontraron cobertura' : 'Families who found coverage'}</h2>
            <p>
              {language === "es"
                ? "Miles de familias ya confian en nosotros para proteger su salud."
                : "Thousands of families already trust us to protect their health."}
            </p>
          </div>
          <div className="health-card-grid three">
            {testimonials.map((item) => (
              <article key={item.name} className="health-testimonial-card">
                <div className="health-stars">★★★★★</div>
                <p>"{item.quote}"</p>
                <div className="health-testimonial-user">
                  <img src={item.img} alt={item.name} />
                  <div>
                    <strong>{item.name}</strong>
                    <span>{item.city}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      {/* <section style={{ padding: '64px 0' }}>
        <div className="hub-container">
          <h2 className="hub-section-h2" style={{ textAlign: 'center', marginBottom: '48px' }}>{language === 'es' ? 'Familias que ya encontraron cobertura' : 'Families who found coverage'}</h2>
          <div className="hub-grid-3">
            {testimonials.map(t => (
              <article key={t.name} style={{ borderRadius: '1rem', border: '1px solid var(--secondary)', background: 'var(--card)', padding: '24px', boxShadow: 'var(--shadow-soft)', transition: 'transform 0.2s' }} onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-4px)')} onMouseLeave={e => (e.currentTarget.style.transform = '')}>
                <div style={{ color: 'var(--success)', fontSize: '1.25rem' }}>★★★★★</div>
                <p style={{ margin: '16px 0 0', fontWeight: 600, lineHeight: 1.6, color: 'var(--navy)' }}>"{t.quote}"</p>
                <p style={{ margin: '20px 0 0', fontWeight: 900, color: 'var(--navy)' }}>{t.name}</p>
                <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--muted-foreground)' }}>{t.city}</p>
              </article>
            ))}
          </div>
        </div>
      </section> */}

      {/* FPL Section - eligibility table */}
      <section className="health-section">
        <div className="health-container lovable-fpl-section">
          <div className="lovable-fpl-badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"></polyline></svg>
            <span>{language === "es" ? "4 de 5 califican" : "4 out of 5 qualify"}</span>
          </div>
          <h2 className="lovable-fpl-title">
            {language === "es" ? "¿Calificas para un Plan de $0?" : "Do you qualify for a $0 Plan?"}
          </h2>
          <p className="lovable-fpl-subtitle">
            {language === "es"
              ? "La elegibilidad se basa en el ingreso de tu hogar. Si tu ingreso está dentro de estos rangos, podrías calificar para un plan sin costo mensual."
              : "Eligibility is based on household income. If your income falls within these ranges, you may qualify for a plan with no monthly cost."}
          </p>

          <div className="lovable-fpl-card">
            <div className="lovable-fpl-table-wrapper">
              <table className="lovable-fpl-table">
                <thead>
                  <tr>
                    {fplTableHeaders.map((header, idx) => (
                      <th key={header} className={idx === 3 ? "highlight-col-header" : ""}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fplTableRows.map((row) => (
                    <tr key={row.size}>
                      <td data-label="Tamaño"><strong>{row.size}</strong></td>
                      <td data-label={language === 'es' ? "100% FPL" : "100% FPL"}>{row.fpl100}</td>
                      <td data-label={language === 'es' ? "150% FPL" : "150% FPL"}>{row.fpl150}</td>
                      <td className="highlight-col-cell" data-label={language === 'es' ? "200% FPL ★" : "200% FPL ★"}><strong>{row.fpl200}</strong></td>
                      <td data-label={language === 'es' ? "400% FPL" : "400% FPL"}>{row.fpl400}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="lovable-fpl-footer">
              <div className="lovable-fpl-footer-text">
                <div className="lovable-fpl-footer-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                </div>
                <p>
                  <strong>{language === "es" ? "★ Columna destacada (200% FPL):" : "★ Featured column (200% FPL):"}</strong>{" "}
                  {language === "es"
                    ? "La mayoría de nuestros clientes califican en este rango o menos."
                    : "Most of our clients qualify in this range or below."}
                </p>
              </div>
              <button type="button" className="lovable-cta-btn" onClick={handleScrollToWizard}>
                {language === "es" ? "Verificar Elegibilidad" : "Check Eligibility"}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="btn-arrow-icon"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
              </button>
            </div>
          </div>
          <p className="lovable-fpl-disclaimer">
            {language === "es"
              ? "Nota: Los niveles exactos de ingreso pueden variar por estado. Datos basados en las guías federales de pobreza 2024."
              : "Note: Exact income levels may vary by state. Data based on 2024 federal poverty guidelines."}
          </p>
        </div>
      </section>
      <section className="health-section">
        <div className="health-container health-narrow">
          <div className="health-section-header">
            <p className="health-overline">{language === "es" ? "Proceso Simple" : "Simple Process"}</p>
            <h2>
              {language === "es" ? "Inscribete en " : "Enroll in "}
              <span>{language === "es" ? "3 Pasos" : "3 Steps"}</span>
            </h2>
            <p>{language === "es" ? "Te guiamos en cada paso del camino." : "We guide you every step of the way."}</p>
          </div>
          <div className="health-steps-grid">
            {stepsContent.map((item) => (
              <article key={item.number} className="health-step-card">
                <div className="health-step-number">{item.number}</div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      {/* Metal tiers */}
      <section id="planes" style={{ padding: '64px 0' }}>
        <div className="hub-container">
          <div style={{ textAlign: 'center', marginBottom: '36px' }}>
            <p style={{ fontWeight: 900, textTransform: 'uppercase', color: 'var(--success)', letterSpacing: '0.08em' }}>
              {language === 'es' ? 'Niveles de cobertura' : 'Coverage tiers'}
            </p>
            <h2 className="hub-section-h2">{language === 'es' ? 'Planes de Metal' : 'Metal Plans'}</h2>
          </div>
          <div className="hub-grid-4">
            {metalTiers.map(tier => (
              <article key={tier.name} style={{ borderRadius: '1rem', border: '1px solid var(--secondary)', background: 'var(--card)', padding: '24px', boxShadow: 'var(--shadow-soft)' }}>
                <div style={{ height: '12px', borderRadius: '999px', marginBottom: '20px', background: `var(--${tier.id})` }} />
                <h3 style={{ margin: '0 0 12px', fontSize: '1.5rem', fontWeight: 900, color: 'var(--navy)' }}>{tier.name}</h3>
                <p style={{ margin: 0, fontWeight: 700, color: 'var(--muted-foreground)' }}>
                  {language === 'es' ? `${tier.planPays} compañía / ${tier.youPay} tú` : `${tier.planPays} plan / ${tier.youPay} you`}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Risks section - navy background */}
      <section id="elegibilidad" style={{ background: 'var(--navy)', padding: '64px 0', color: 'white' }}>
        <div className="hub-container">
          <h2 style={{ fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', fontWeight: 900, color: 'white', marginBottom: '32px', textAlign: 'center' }}>
            {language === 'es' ? 'No tener cobertura puede salir caro' : 'Not having coverage can be costly'}
          </h2>
          <div className="hub-grid-3">
            {riskCards.map(item => (
              <article key={item.title} style={{ borderRadius: '1rem', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.08)', padding: '24px' }}>
                <div style={{ fontSize: '2.5rem' }}>⚠️</div>
                <h3 style={{ margin: '16px 0 12px', fontSize: '1.5rem', fontWeight: 900, color: 'white' }}>{item.title}</h3>
                <p style={{ margin: 0, lineHeight: 1.6, color: 'rgba(255,255,255,0.78)' }}>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <main className="app-body">
        <section className="page-content health-form-section" id="quote-wizard">
          <div className="health-section-header centered">
            <h2>
              {language === "es" ? "Obten tu " : "Get your "}
              <span>{language === "es" ? "Cotizacion Gratis" : "Free Quote"}</span>
            </h2>
            <p>
              {language === "es"
                ? "Completa los pasos y recibe acompanamiento para encontrar el mejor plan."
                : "Complete the steps and get support finding the best plan."}
            </p>
          </div>
          {apiError && <div className="form-error major-error">{apiError}</div>}
          {/* Stepper visual de fases */}
          {wizardStep !== "quoteSuccess" && (
            <div className="wizard-progress">
              <div className="wizard-stepper" role="progressbar" aria-label="Progreso del formulario">
                <div
                  className="wizard-stepper-fill"
                  style={{ width: `${stepperFillPct}%` }}
                />
                {visiblePhases.map((phase, idx) => {
                  const isDone = idx < currentPhaseIndex;
                  const isActive = idx === currentPhaseIndex;
                  return (
                    <div
                      key={phase.key}
                      className={`wizard-phase${isDone ? " done" : isActive ? " active" : ""}`}
                    >
                      <div className={`wizard-phase-dot${isDone ? " done" : isActive ? " active" : ""}`}>
                        {isDone ? "✓" : idx + 1}
                      </div>
                      <div className="wizard-phase-name">{phase.label}</div>
                    </div>
                  );
                })}
              </div>
              {currentPhaseIndex >= 0 && (
                <div className="wizard-step-counter">
                  {t("wizard.stepCounter", {
                    current: String(currentPhaseIndex + 1),
                    total: String(visiblePhases.length),
                    phase: visiblePhases[currentPhaseIndex]?.label ?? "",
                  })}
                </div>
              )}
            </div>
          )}
          <div className="card-wizard">
            <section className="page-heading">
              <div>
                <h2>{pageTitle}</h2>
              </div>
            </section>
            {wizardStep === "householdType" && (
              <HouseholdTypeStep
                value={householdType}
                onSelect={handleSelectHouseholdType}
              />
            )}

            {wizardStep === "householdBasics" && householdType && (
              <HouseholdBasicsStep
                householdType={householdType}
                initialZip={metadata.household.zipCode}
                selectedHouseholdTypeIcon={selectedHouseholdTypeIcon}
                initialIncome={metadata.household.income}
                initialCountyFips={metadata.household.countyFips}
                initialEffectiveDate={metadata.household.effectiveDate}
                onBack={() => setWizardStep("householdType")}
                onSubmit={handleHouseholdBasicsSubmit}
              />
            )}

            {wizardStep === "primary" && householdType && (
              <PrimaryApplicantStep
                selectedHouseholdTypeIcon={selectedHouseholdTypeIcon}
                householdType={householdType}
                initialValues={currentPrimary}
                onBack={() => setWizardStep("householdBasics")}
                onSubmit={handlePrimarySubmit}
              />
            )}

            {wizardStep === "members" && householdType && (
              <AdditionalMembersStep
                selectedHouseholdTypeIcon={selectedHouseholdTypeIcon}
                householdType={householdType}
                members={metadata.members}
                onBack={() => setWizardStep("primary")}
                onSubmit={handleMembersSubmit}
              />
            )}

            {wizardStep === "identityOtp" && (
              <QuoteOtpStep
                onSend={handleRequestIdentityOtp}
                onVerify={handleVerifyIdentityOtp}
                isSending={isIdentityOtpSending}
                isVerifying={isIdentityOtpVerifying}
                otpSent={identityOtpStatus.sent}
                smsSent={identityOtpStatus.smsSent}
                emailSent={identityOtpStatus.emailSent}
                error={apiError}
                leadEmail={currentPrimary?.email ?? null}
                leadPhone={currentPrimary?.phone ?? null}
                translationPrefix="identity.otp"
              />
            )}

            {wizardStep === "plans" && currentPrimary && (<>
              <PlanSelectionPage
                metadata={metadata}
                onBack={() =>
                  householdNeedsAdditionalMembers(householdType)
                    ? setWizardStep("members")
                    : setWizardStep("primary")
                }
                onPlanSelect={handlePlanSelect}
              /><div className="social-proof-bar" style={{ marginBottom: "24px" }}>
                <div className="social-proof-item">
                  <span className="social-proof-icon">🔒</span>
                  <span>
                    {language === "es" ? "Gratis y sin compromiso" : "Free & no commitment"}
                  </span>
                </div>
                <div className="social-proof-divider" />
                <div className="social-proof-item">
                  <span className="social-proof-icon">🛡️</span>
                  <span>
                    {language === "es" ? "256-bit encriptado" : "256-bit encrypted"}
                  </span>
                </div>
                <div className="social-proof-divider" />
                <div className="social-proof-item">
                  <span className="social-proof-icon">✅</span>
                  <span>
                    {language === "es" ? "Agente licenciado ACA" : "Licensed ACA agent"}
                  </span>
                </div>
              </div></>
            )}

            {wizardStep === "summary" && selectedPlan && currentPrimary && (
              <PlanSummaryPage
                metadata={metadata}
                plan={selectedPlan}
                primary={currentPrimary}
                consentStatus={consentStatus}
                language={language}
                signingLink={signingLink}
                consentHtml={consentHtml}
                onBack={() => setWizardStep("plans")}
                onConfirm={handleConfirmPlan}
                onCheckConsent={
                  consentStatus === "SIGNED"
                    ? undefined
                    : () => handleCheckConsent(false)
                }
                isSubmitting={isSubmitting}
                isCheckingConsent={isCheckingConsent}
              />
            )}

            {wizardStep === "consent" && selectedPlan && currentPrimary && (
              <PlanSummaryPage
                metadata={metadata}
                plan={selectedPlan}
                primary={currentPrimary}
                consentStatus={consentStatus}
                signingLink={signingLink}
                consentHtml={consentHtml}
                onBack={() => setWizardStep("summary")}
                onConfirm={() => { }}
                onCheckConsent={() => handleCheckConsent(false)}
                isSubmitting={false}
                isCheckingConsent={isCheckingConsent}
              />
            )}

            {wizardStep === "quoteHolder" && (
              <QuoteHolderStep
                selectedHouseholdTypeIcon={selectedHouseholdTypeIcon}
                initialValues={quoteHolderState}
                onSubmit={handleQuoteHolderSubmit}
                onBack={() => setWizardStep("consent")}
                isSubmitting={false}
              />
            )}

            {wizardStep === "quoteMembers" && (
              <QuoteMembersStep
                selectedHouseholdTypeIcon={selectedHouseholdTypeIcon}
                initialMembers={quoteMembersState}
                onSubmit={handleQuoteMembersSubmit}
                onBack={() => setWizardStep("quoteHolder")}
                isSubmitting={false}
              />
            )}

            {wizardStep === "quotePayment" && (
              <QuotePaymentStep
                initialValues={quotePaymentState}
                onSubmit={handleQuotePaymentSubmit}
                onBack={() =>
                  quoteMembersState.length > 0
                    ? setWizardStep("quoteMembers")
                    : setWizardStep("quoteHolder")
                }
                isSubmitting={isCreatingQuote}
                error={apiError}
              />
            )}

            {wizardStep === "quoteSuccess" && (
              <QuoteSuccessStep
                policyId={quotePolicyId}
                selectedHouseholdTypeIcon={selectedHouseholdTypeIcon}
                planName={successPlanName}
                planCarrier={successPlanCarrier}
                planPremium={
                  successPlanPremium !== null &&
                    Number.isFinite(successPlanPremium)
                    ? successPlanPremium
                    : null
                }
                onFinish={handleQuoteFinish}
              />
            )}
          </div>

          {isSubmitting && !["summary", "consent"].includes(wizardStep) && (
            <div className="loading-overlay">
              <Spinner animation="border" role="status" />
              <span>{t("wizard.loading")}</span>
            </div>
          )}
        </section>


      </main>



      {/* FAQ */}
      <section id="faq" style={{ padding: '64px 0' }}>
        <div className="hub-container" style={{ maxWidth: '56rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <p style={{ fontWeight: 900, textTransform: 'uppercase', color: 'var(--success)', letterSpacing: '0.08em' }}>
              {language === 'es' ? 'Preguntas frecuentes' : 'FAQ'}
            </p>
            <h2 className="hub-section-h2">
              {language === 'es' ? 'Respuestas claras antes de cotizar' : 'Clear answers before quoting'}
            </h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {faqItems.map(item => (
              <details key={item.q} style={{ borderRadius: '1rem', border: '1px solid var(--secondary)', background: 'var(--card)', padding: '24px', boxShadow: 'var(--shadow-soft)' }}>
                <summary style={{ cursor: 'pointer', fontSize: '1.125rem', fontWeight: 900, color: 'var(--navy)', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {item.q}<span style={{ color: 'var(--success)', fontSize: '1.5rem', lineHeight: 1 }}>+</span>
                </summary>
                <p style={{ margin: '16px 0 0', lineHeight: 1.6, color: 'var(--muted-foreground)' }}>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* 
      <section className="health-section">
        <div className="health-container health-faq">
          <div className="health-section-header">
            <p className="health-overline">{language === "es" ? "Preguntas Frecuentes" : "Frequently Asked Questions"}</p>
            <h2>
              {language === "es" ? "Tienes " : "Have "}
              <span>{language === "es" ? "Dudas?" : "Questions?"}</span>
            </h2>
          </div>
          <div className="health-faq-list">
            {faqItems.map((item) => (
              <details key={item.q} className="health-faq-item">
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section> */}

      <section className="health-final-cta">
        <div className="health-container health-final-cta-inner">
          <h2>{language === "es" ? "Listo Para Empezar?" : "Ready to Get Started?"}</h2>
          <p>
            {language === "es"
              ? "Habla con un agente licenciado hoy y encuentra el plan perfecto para ti y tu familia."
              : "Speak with a licensed agent today and find the right plan for you and your family."}
          </p>
          <div className="health-hero-actions centered">
            <a className="health-call-button" href={`tel:${displayPhone}`}>
              <FontAwesomeIcon icon={faPhone} />
              <span style={{ whiteSpace: 'nowrap' }}>{displayPhone}</span>
            </a>
            <button type="button" className="health-outline-button" onClick={handleScrollToWizard}>
              {language === "es" ? "Obten Una Cotizacion" : "Get A Quote"}
            </button>
          </div>
        </div>
      </section>

      {/* Navy footer */}
      <footer style={{ background: 'var(--navy)', padding: '48px 24px', color: 'white' }}>
        <div className="hub-container">
          <div style={{ display: 'grid', gap: '32px', gridTemplateColumns: '1fr 1.2fr' }}>
            <div>
              <p style={{ fontSize: '0.875rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--success)', letterSpacing: '0.08em' }}>
                {language === 'es' ? 'Llámanos ahora' : 'Call us now'}
              </p>
              <a href={`tel:${displayPhone}`} style={{ display: 'block', marginTop: '8px', fontSize: '2rem', fontWeight: 900, color: 'white', textDecoration: 'none', whiteSpace: 'nowrap' }}>{displayPhone}</a>
              <p style={{ marginTop: '16px', fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)' }}>
                © {new Date().getFullYear()} {language === 'es' ? 'Seguros de Salud en Español' : 'Health Insurance in Spanish'}.
              </p>
            </div>
            <p style={{ fontSize: '0.875rem', lineHeight: 1.6, color: 'rgba(255,255,255,0.72)', alignSelf: 'center' }}>
              {t("footer.line1")}
            </p>
          </div>
        </div>
      </footer>

      <div className="health-sticky-mobile">
        <a className="health-call-button" href={`tel:${displayPhone}`}>
          <FontAwesomeIcon icon={faPhone} />
          <span style={{ whiteSpace: 'nowrap' }}>{displayPhone}</span>
        </a>
        <button type="button" className="health-outline-button green" onClick={handleScrollToWizard}>
          {language === "es" ? "INSCRIBIRME" : "ENROLL NOW"}
        </button>
      </div>

      {
        process.env.REACT_APP_WHATSAPP_NUMBER && (
          <a
            href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}`}
            target="_blank"
            rel="noopener noreferrer"
            className="whatsapp-floating-button"
            aria-label="Contact us on WhatsApp"
          >
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              className="whatsapp-icon"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
          </a>
        )
      }
    </div >
  );
};

export default App;
